import React from 'react';
import { Check, CirclePlus, SlidersHorizontal, X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import i18n from '@/utils/i18n';

export type FacetOption = { value: string; label: string };

type ContainerFacetChipProps = {
  label: string;
  options: FacetOption[];
  /** Currently selected option values. */
  selected: string[];
  onChange: (next: string[]) => void;
  /** Open the popover on mount (used right after "Add filter" adds a chip). */
  defaultOpen?: boolean;
  /** Called by the clear × when the chip is removable ("Add filter" chips). */
  onRemove?: () => void;
};

/**
 * A pill that is both a filter's status and its trigger. Idle: dashed outline +
 * "add" icon. Active: solid outline, a count badge, and a per-facet clear (×).
 * Selecting options toggles them; the parent drives the table's column filters.
 */
export default function ContainerFacetChip({
  label,
  options,
  selected,
  onChange,
  defaultOpen = false,
  onRemove,
}: ContainerFacetChipProps) {
  const [open, setOpen] = React.useState(defaultOpen);
  const hasValues = selected.length > 0;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    // A removable ("Add filter") chip that closes without any selection
    // disappears again instead of lingering as an empty chip.
    if (!nextOpen && selected.length === 0) {
      onRemove?.();
    }
  };

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="tw:relative tw:inline-flex">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <div
          className={cn(
            'tw:inline-flex tw:h-9 tw:items-center tw:rounded-md',
            hasValues
              ? 'tw:border tw:border-border tw:bg-background tw:shadow-xs'
              : 'tw:border tw:border-dashed tw:border-border tw:bg-transparent',
          )}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'tw:inline-flex tw:h-full tw:items-center tw:gap-2 tw:rounded-md tw:px-3 tw:text-sm tw:font-medium tw:outline-none tw:cursor-pointer',
                hasValues ? 'tw:text-foreground' : 'tw:text-muted-foreground',
              )}
            >
              {hasValues ? (
                <SlidersHorizontal size={14} />
              ) : (
                <CirclePlus size={14} />
              )}
              {label}
              {hasValues && (
                <span className="tw:inline-flex tw:h-[18px] tw:min-w-[18px] tw:items-center tw:justify-center tw:rounded-sm tw:bg-primary tw:px-1 tw:text-xs tw:font-bold tw:text-primary-foreground">
                  {selected.length}
                </span>
              )}
            </button>
          </PopoverTrigger>
          {hasValues && (
            <button
              type="button"
              aria-label={i18n.t('EditContainerInfo:clearFacet', {
                facet: label,
              })}
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
                onRemove?.();
              }}
              className="tw:flex tw:h-full tw:w-7 tw:items-center tw:justify-center tw:border-l tw:border-border tw:text-muted-foreground tw:outline-none tw:cursor-pointer tw:hover:text-foreground"
            >
              <X size={13} />
            </button>
          )}
        </div>
        <PopoverContent
          align="start"
          className="tw:w-[232px] tw:gap-0 tw:p-1.5"
        >
          <div className="tw:px-2 tw:pt-1.5 tw:pb-2 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
            {i18n.t('EditContainerInfo:filterByFacet', { facet: label })}
          </div>
          <div className="tw:flex tw:max-h-[300px] tw:flex-col tw:overflow-auto">
            {options.length === 0 && (
              <div className="tw:px-2 tw:py-3 tw:text-sm tw:text-muted-foreground">
                {i18n.t('Common:noResultsFound')}
              </div>
            )}
            {options.map((option) => {
              const checked = selected.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggle(option.value)}
                  className="tw:flex tw:w-full tw:items-center tw:gap-2.5 tw:rounded-sm tw:px-2 tw:py-1.5 tw:text-left tw:text-sm tw:text-foreground tw:outline-none tw:cursor-pointer tw:hover:bg-accent"
                >
                  <span
                    className={cn(
                      'tw:flex tw:size-[17px] tw:flex-none tw:items-center tw:justify-center tw:rounded-[4px] tw:border',
                      checked
                        ? 'tw:border-primary tw:bg-primary tw:text-primary-foreground'
                        : 'tw:border-input',
                    )}
                  >
                    {checked && <Check size={12} />}
                  </span>
                  <span className="tw:truncate">{option.label}</span>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
